import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { shouldRedirectToMobile } from "@/utils/deviceDetection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { UtensilsCrossed, Mail, Lock, User, Globe, Clock, AlertCircle, CheckCircle } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const { login, register } = useAuth();
  const { t, language, setLanguage, availableLanguages } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setPendingApproval(false);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success(t('welcomeBack'));
        
        // Redirect based on device type
        if (shouldRedirectToMobile()) {
          navigate("/mobile");
        } else {
          navigate("/");
        }
      } else {
        const result = await register(email, password, name);
        // Check if pending_approval flag is set
        if (result?.pending_approval) {
          setRegistrationSuccess(true);
          toast.success(
            language === 'en' 
              ? "Registration successful! Awaiting admin approval." 
              : language === 'ht'
              ? "Enskripsyon reyisi! Ap tann apwobasyon admin."
              : "¡Registro exitoso! Esperando aprobación del administrador."
          );
        } else {
          toast.success(t('menuCreated'));
          // Redirect based on device type
          if (shouldRedirectToMobile()) {
            navigate("/mobile");
          } else {
            navigate("/");
          }
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Error al procesar la solicitud";
      // Check if the error is about pending approval
      if (message.includes("PENDING_APPROVAL") || message.toLowerCase().includes("pending") || message.toLowerCase().includes("aprobación") || message.toLowerCase().includes("approval")) {
        setPendingApproval(true);
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Hero image */}
      <div 
        className="hidden lg:flex lg:w-1/2 login-hero relative"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200')"
        }}
      >
        <div className="absolute inset-0 bg-stone-900/60" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <img 
            src="/assets/logo-sally.png" 
            alt="Sally Better" 
            className="h-24 w-auto mb-6"
          />
          <p className="text-xl text-stone-200 max-w-md">
            {t('manageMenus')}
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-stone-50">
        <Card className="w-full max-w-md border-stone-200 shadow-sm">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-between mb-4">
              <div className="lg:hidden">
                <img 
                  src="/assets/logo-sally.png" 
                  alt="Sally Better" 
                  className="h-10 w-auto invert"
                />
              </div>
              <LanguageSelector variant="dark" showLabel={true} />
            </div>
            <CardTitle className="text-2xl font-semibold text-stone-900">
              {isLogin ? t('login') : t('register')}
            </CardTitle>
            <CardDescription className="text-stone-500">
              {isLogin ? t('loginDescription') : t('createAccount')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Pending Approval Alert */}
            {pendingApproval && (
              <Alert className="mb-4 border-amber-200 bg-amber-50">
                <Clock className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">
                  {language === 'en' ? 'Account Pending Approval' : 'Cuenta Pendiente de Aprobación'}
                </AlertTitle>
                <AlertDescription className="text-amber-700">
                  {language === 'en' 
                    ? 'Your account is awaiting administrator approval. You will receive an email once approved.' 
                    : 'Tu cuenta está esperando aprobación del administrador. Recibirás un email cuando sea aprobada.'}
                </AlertDescription>
              </Alert>
            )}

            {/* Registration Success Alert */}
            {registrationSuccess && (
              <Alert className="mb-4 border-emerald-200 bg-emerald-50">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="text-emerald-800">
                  {language === 'en' ? 'Registration Successful!' : '¡Registro Exitoso!'}
                </AlertTitle>
                <AlertDescription className="text-emerald-700">
                  {language === 'en' 
                    ? 'Your account has been created. An administrator will review and approve your account shortly.' 
                    : 'Tu cuenta ha sido creada. Un administrador revisará y aprobará tu cuenta pronto.'}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-stone-700">{t('name')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder={t('name')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 bg-stone-50 border-stone-200 focus:ring-stone-400"
                      required={!isLogin}
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-700">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-stone-50 border-stone-200 focus:ring-stone-400"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-stone-700">{t('password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-stone-50 border-stone-200 focus:ring-stone-400"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-stone-900 hover:bg-stone-800 text-stone-50 transition-all duration-200 active:scale-95"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? t('loading') : (isLogin ? t('login') : t('register'))}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-stone-600 hover:text-stone-900 underline-offset-4 hover:underline transition-colors"
                data-testid="toggle-auth-mode-btn"
              >
                {isLogin ? t('createAccount') : t('alreadyHaveAccount')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
